import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Activity, BookOpen, Boxes, FileCheck2, CalendarRange, Send, ChevronRight, CreditCard, Globe2, Images, KeyRound, LayoutDashboard, Languages, LogOut, Menu, Monitor, Moon, ScrollText, Search, Sun, Users } from "lucide-react";
import { admin, api } from "./lib/api";
import { useStudio, type ThemeMode } from "./lib/context";
import { TelegramDialog } from "./components/TelegramDialog";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Curriculum } from "./pages/Curriculum";
import { NotionPage } from "./pages/Notion";
import { AnnalesPage, PaperPage, QuestionPage } from "./pages/Annales";
import { Glossary } from "./pages/Glossary";
import { Media } from "./pages/Media";
import { Bank } from "./pages/Bank";
import { SystemPage, canSeeSystem } from "./pages/System";
import { ROLE_INFO } from "@jange/shared";
import { BrandMark } from "./components/BrandMark";
import { CommandPalette } from "./components/CommandPalette";
import { Avatar, flagOf } from "./components/ui";

const ROLE_LABEL: Record<string, string> = Object.fromEntries(Object.entries(ROLE_INFO).map(([k, v]) => [k, v.label]));
export const hasContentRole = (roles: readonly string[]) => roles.some((r) => (ROLE_INFO as Record<string, { family: string }>)[r]?.family === "content");

export function App() {
  const { teacher, setPaletteOpen } = useStudio();
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setNavOpen(false), [loc.pathname]);
  // ⌘K / Ctrl+K partout ; « / » hors des champs de saisie.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable]");
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setPaletteOpen(true); }
      else if (e.key === "/" && !typing) { e.preventDefault(); setPaletteOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPaletteOpen]);

  if (!teacher) return <Login />;
  // Suivi technique seul : pas d'accès au contenu, on arrive directement sur la partie système.
  const content = hasContentRole(teacher.roles);
  const wide = loc.pathname.startsWith("/notions/");
  return (
    <div className={`app ${navOpen ? "nav-open" : ""}`}>
      <a className="skip" href="#contenu">Aller au contenu</a>
      <Sidebar />
      {navOpen && <div className="backdrop" style={{ zIndex: 25 }} onClick={() => setNavOpen(false)} />}
      <div className="workspace">
        <Topbar onMenu={() => setNavOpen(true)} />
        {/* L'éditeur de notion a sa propre barre d'actions en bas : pas d'onglets par-dessus. */}
        {!wide && <TabBar content={content} system={canSeeSystem(teacher.roles)} onMore={() => setNavOpen(true)} />}
        <main id="contenu" className={`content ${wide ? "" : "narrow"}`} tabIndex={-1}>
          <Routes>
            <Route path="/" element={content ? <Dashboard /> : <Navigate to="/systeme" replace />} />
            <Route path="/programme" element={<Curriculum />} />
            <Route path="/notions/:id" element={<NotionPage />} />
            <Route path="/annales" element={<AnnalesPage />} />
            <Route path="/annales/sujets/:id" element={<PaperPage />} />
            <Route path="/annales/questions/:id" element={<QuestionPage />} />
            <Route path="/glossaire" element={<Glossary />} />
            <Route path="/medias" element={<Media />} />
            <Route path="/banque" element={<Bank />} />
            {canSeeSystem(teacher.roles) && <Route path="/systeme/*" element={<SystemPage />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}

const navCls = ({ isActive }: { isActive: boolean }) => `nav ${isActive ? "active" : ""}`;

function Sidebar() {
  const { teacher, signOut, countryId, setCountryId, setPaletteOpen } = useStudio();
  const [telegramOpen, setTelegramOpen] = useState(false);
  const content = !!teacher && hasContentRole(teacher.roles);
  const system = !!teacher && canSeeSystem(teacher.roles);
  const dash = useQuery({ queryKey: ["dashboard", countryId], queryFn: () => api.dashboard(countryId), enabled: content });
  const countries = useQuery({ queryKey: ["countries"], queryFn: api.countries, staleTime: 60 * 60_000 });
  const me = useQuery({ queryKey: ["admin-me"], queryFn: admin.me, enabled: system });
  const perms = me.data?.permissions ?? [];
  const pending = dash.data?.reviewQueue.length ?? 0;
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  // Pied de barre : nom sans la parenthèse (« Aminata Diallo (autrice) » → « Aminata Diallo »), rôles sur une ligne.
  const shortName = (teacher?.displayName ?? "").replace(/\s*\(.*?\)\s*$/, "");
  const roleLabels = (teacher?.roles ?? []).map((r) => ROLE_LABEL[r] ?? r);
  const rolesLine = roleLabels.join(" · ") + (teacher?.languages.length ? ` (${teacher.languages.join(", ")})` : "");
  const rolesShort = roleLabels.length > 2 ? `${roleLabels.slice(0, 2).join(" · ")} +${roleLabels.length - 2}` : rolesLine;
  return (
    <aside className="sidebar" aria-label="Navigation principale">
      <Link to="/" className="brand"><BrandMark size={30} /><span>Jang-e<small>Studio enseignants</small></span></Link>
      <button type="button" className="search-trigger" onClick={() => setPaletteOpen(true)} aria-label="Rechercher (raccourci Ctrl K)">
        <Search size={15} /><span>Rechercher…</span><span className="kbd">{mac ? "⌘" : "Ctrl"} K</span>
      </button>
      {content && <>
        <div className="nav-group">Contenu</div>
        <NavLink to="/" end className={navCls}><LayoutDashboard size={17} />Tableau de bord{pending > 0 && <span className="count" aria-label={`${pending} à relire`}>{pending}</span>}</NavLink>
        <NavLink to="/programme" className={navCls}><BookOpen size={17} />Programme</NavLink>
        <NavLink to="/annales" className={navCls}><FileCheck2 size={17} />Annales</NavLink>
        <NavLink to="/banque" className={navCls}><Boxes size={17} />Banque 3D</NavLink>
        <NavLink to="/medias" className={navCls}><Images size={17} />Médiathèque</NavLink>
        <NavLink to="/glossaire" className={navCls}><Languages size={17} />Glossaire</NavLink>
      </>}
      {/* Partie système : invisible pour les collaborateurs contenu (et refusée par l'API). */}
      {system && <>
        <div className="nav-group">Administration</div>
        <NavLink to="/systeme" end className={navCls}><Activity size={17} />État du service</NavLink>
        <NavLink to="/systeme/pays" className={navCls}><Globe2 size={17} />Pays</NavLink>
        <NavLink to="/systeme/formules" className={navCls}><CreditCard size={17} />Formules</NavLink>
        <NavLink to="/systeme/fournisseurs" className={navCls}><KeyRound size={17} />Fournisseurs</NavLink>
        {perms.includes("reference.manage") && <NavLink to="/systeme/referentiel" className={navCls}><CalendarRange size={17} />Référentiel</NavLink>}
        {perms.includes("team.view") && <NavLink to="/systeme/equipe" className={navCls}><Users size={17} />Équipe</NavLink>}
        {perms.includes("team.view") && <NavLink to="/systeme/journal" className={navCls}><ScrollText size={17} />Journal</NavLink>}
      </>}
      <div className="side-foot">
        {(countries.data?.length ?? 0) > 1 && (
          <label className="field">
            <span className="tiny" style={{ color: "var(--text-3)" }}>Pays de travail</span>
            <select className="select" value={countryId} onChange={(e) => setCountryId(e.target.value)} aria-label="Pays de travail">
              {countries.data!.map((c) => <option key={c.id} value={c.id}>{flagOf(c.id)} {c.name}</option>)}
            </select>
          </label>
        )}
        <div className="who" title={`${teacher?.displayName ?? ""}\n${rolesLine}`}>
          <Avatar name={teacher?.displayName} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <b>{shortName}</b>
            <div className="tiny muted">{rolesShort}</div>
          </div>
          <div className="actions">
            <button className="icon-btn" onClick={() => setTelegramOpen(true)} aria-label="Codes de connexion par Telegram" title="Codes de connexion par Telegram"><Send size={15} /></button>
            <button className="icon-btn" onClick={signOut} aria-label="Se déconnecter" title="Se déconnecter"><LogOut size={15} /></button>
          </div>
        </div>
      </div>
      {telegramOpen && <TelegramDialog onClose={() => setTelegramOpen(false)} />}
    </aside>
  );
}

const SECTION: [RegExp, string][] = [
  [/^\/programme/, "Programme"], [/^\/medias/, "Médiathèque"], [/^\/banque/, "Banque 3D"], [/^\/glossaire/, "Glossaire"],
  [/^\/systeme\/pays/, "Pays"], [/^\/systeme\/formules/, "Formules"], [/^\/systeme\/fournisseurs/, "Fournisseurs"], [/^\/systeme\/equipe/, "Équipe"], [/^\/systeme\/journal/, "Journal"], [/^\/systeme/, "État du service"],
  [/^\/$/, "Tableau de bord"],
];
const THEMES: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: "light", label: "Thème clair", Icon: Sun }, { mode: "dark", label: "Thème sombre", Icon: Moon }, { mode: "system", label: "Thème du système", Icon: Monitor },
];

/** Barre d'onglets du bas, sur téléphone : les sections les plus utilisées, « Plus » ouvre le menu complet. */
function TabBar({ content, system, onMore }: { content: boolean; system: boolean; onMore: () => void }) {
  const { countryId } = useStudio();
  const dash = useQuery({ queryKey: ["dashboard", countryId], queryFn: () => api.dashboard(countryId), enabled: content });
  const pending = dash.data?.reviewQueue.length ?? 0;
  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : "");
  return (
    <nav className="tabbar" aria-label="Sections">
      {content ? <>
        <NavLink to="/" end className={cls}><LayoutDashboard size={20} />Accueil{pending > 0 && <span className="count" aria-label={`${pending} à relire`}>{pending}</span>}</NavLink>
        <NavLink to="/programme" className={cls}><BookOpen size={20} />Programme</NavLink>
        <NavLink to="/banque" className={cls}><Boxes size={20} />Banque 3D</NavLink>
        <NavLink to="/medias" className={cls}><Images size={20} />Médias</NavLink>
      </> : <>
        <NavLink to="/systeme" end className={cls}><Activity size={20} />État</NavLink>
        <NavLink to="/systeme/pays" className={cls}><Globe2 size={20} />Pays</NavLink>
        <NavLink to="/systeme/fournisseurs" className={cls}><KeyRound size={20} />Clés</NavLink>
        {system && <NavLink to="/systeme/equipe" className={cls}><Users size={20} />Équipe</NavLink>}
      </>}
      <button type="button" onClick={onMore} aria-label="Ouvrir le menu complet"><Menu size={20} />Plus</button>
    </nav>
  );
}

function Topbar({ onMenu }: { onMenu: () => void }) {
  const { crumbs, theme, setTheme, setPaletteOpen } = useStudio();
  const loc = useLocation();
  const section = SECTION.find(([re]) => re.test(loc.pathname))?.[1];
  const items = crumbs.length ? crumbs : section ? [{ label: section }] : [];
  const admin = loc.pathname.startsWith("/systeme");
  const cur = THEMES.find((t) => t.mode === theme) ?? THEMES[2]!;
  const next = THEMES[(THEMES.indexOf(cur) + 1) % THEMES.length]!;
  return (
    <header className="topbar">
      <button className="icon-btn bordered menu-btn" onClick={onMenu} aria-label="Ouvrir le menu"><Menu size={17} /></button>
      <nav className="crumbs" aria-label="Fil d'Ariane">
        <span>{admin ? "Administration" : "Studio"}</span>
        {items.map((c, i) => (
          <span key={i} className="row" style={{ gap: 6, minWidth: 0 }}>
            <ChevronRight size={14} />
            {c.to && i < items.length - 1 ? <Link to={c.to}>{c.label}</Link> : <span className="here" aria-current="page">{c.label}</span>}
          </span>
        ))}
      </nav>
      <span className="spacer" />
      <button className="icon-btn bordered" onClick={() => setPaletteOpen(true)} aria-label="Rechercher"><Search size={16} /></button>
      <button className="icon-btn bordered" onClick={() => setTheme(next.mode)} aria-label={`${cur.label} — passer au ${next.label.toLowerCase()}`} title={`${cur.label} (cliquer : ${next.label.toLowerCase()})`}><cur.Icon size={16} /></button>
    </header>
  );
}
